import { accountsSlice } from 'app/store';
import { gotoNode } from 'GeminiViewerComponent/components/Procedure/features/procedureSlice';
import { getDependencyContents } from 'GeminiViewerComponent/features/asset/assetSlice';
import { setCompanionApp } from 'GeminiViewerComponent/features/companionApps/companionAppsSlice';
import { getConfig } from 'GeminiViewerComponent/features/config/configSlice';
import { findVideoChapter, setLinkDisplayVis } from 'GeminiViewerComponent/features/globals/visibilitySlice';
import { LrsVerbs } from 'GeminiViewerComponent/features/xAPI/xApiData';
import { saveStatement } from 'GeminiViewerComponent/features/xAPI/xapiSlice';
import { prefixUrl } from 'GeminiViewerComponent/helpers';
import { getPdfjsViewerUrl } from 'GeminiViewerComponent/helpers/urlUtils';
import interceptClicks from 'GeminiViewerComponent/helpers/interceptClicks';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useZoneNav } from 'shared/hooks/useZoneNav';

const { selectActiveUser } = accountsSlice;

export default function useContentLinkInterceptor(selector, contentService, handleNext = null, handleBack = null) {
    const dispatch = useDispatch();
    const config = useSelector(getConfig);
    const dependencyContents = useSelector(getDependencyContents);
    const activeUser = useSelector(selectActiveUser);
    const { gotoZone, gotoZoneAndItem } = useZoneNav();

    useEffect(() => {
        const handleContentLinkClick = async function (e, el) {
            e.preventDefault();
            const params = new URLSearchParams(el.search);
            const contentId = parseInt(params.get('content_id') ?? el.dataset.targetContentid);
            const autoPlay = params.get('auto_play') ?? el.getAttribute('data-target-auto-play');
            const linkTypeId = parseInt(params.get('link_type_id'))
                || parseInt(el.getAttribute('data-target-link-type-id'))
                || 2;
            const target = el.getAttribute('target');

            dispatch(
                saveStatement({
                    LrsVerb: LrsVerbs.Interacted,
                    response: JSON.stringify({
                        contentId: contentId,
                        autoPlay: autoPlay,
                        linkTypeId: linkTypeId,
                    }),
                    contentId,
                })
            );

            let content;
            if (contentId) {
                if (config.modeType === 'viewer') {
                    content = dependencyContents.find((c) => c.content_id === contentId);
                } else {
                    content = await contentService.fetchContentById(contentId, activeUser.s3_prefix);
                }
            }

            let contentVersion = content?.last_content_version;
            if (!contentVersion && content?.content_versions) {
                contentVersion = content.content_versions.toSorted((a, b) => b.version - a.version)[0];
            }
            if (contentVersion) {
                const rawUrl = contentVersion.embed_data
                    ? contentVersion.embed_data
                    : prefixUrl(
                          contentVersion.encoding_status?.toLowerCase() === 'complete'
                              ? contentVersion.encoded_url
                              : contentVersion.url,
                          activeUser.s3_prefix,
                          contentVersion?.launch_file
                      );

                const isPdfContent =
                    linkTypeId === 1 /* LinkTypeIds.Document */ &&
                    content?.content_type_id === 7; /* ContentTypes.Pdf.id */

                const url = isPdfContent ? getPdfjsViewerUrl(rawUrl) : rawUrl;

                if (target === 'companion_app') {
                    const companionData = {
                        display_name: el.textContent,
                        app_url: url,
                        app_mode: 1,
                        linkType: linkTypeId,
                        linkData: contentVersion,
                        skill_id: content.skill_id,
                    };
                    dispatch(setCompanionApp(companionData));
                } else {
                    const chapterId = el.getAttribute('data-chapter-id');
                    if (chapterId) {
                        contentVersion.chapter_id = chapterId;
                        contentVersion.show_chapters = true;
                    }
                    dispatch(
                        setLinkDisplayVis({
                            open: true,
                            linkTypeId: linkTypeId ?? content.default_link_type_id,
                            linkContent: url,
                            linkData: {
                                display_name: content.display_name,
                                additional_data: content.additional_data,
                                ...contentVersion,
                            },
                            chapter_id: chapterId,
                            autoPlay: autoPlay,
                            overlayText: contentVersion.overlay_text,
                        })
                    );
                }
            } else {
                let nodeId = params.get('node_id');
                let assetId = params.get('asset_id');
                let zoneId = params.get('zone_id');
                let itemId = params.get('item_id');
                let linkId = params.get('link_id');
                if (nodeId) {
                    if (zoneId) {
                        if (itemId) {
                            dispatch(
                                saveStatement({
                                    LrsVerb: LrsVerbs.Progressed,
                                    response: { zoneId, itemId },
                                    zoneId,
                                    itemId,
                                })
                            );
                            gotoZoneAndItem(zoneId, itemId, linkId, assetId);
                        } else {
                            dispatch(
                                saveStatement({
                                    LrsVerb: LrsVerbs.Progressed,
                                    response: { nodeId, zoneId },
                                    zoneId,
                                })
                            );
                            gotoZone(zoneId);
                        }
                    }
                    if (nodeId === 'next') {
                        handleNext?.(null, null);
                    } else if (nodeId === 'previous') {
                        handleBack?.(null, null);
                    } else {
                        dispatch(gotoNode(Number(nodeId)));
                    }
                } else if (zoneId) {
                    if (itemId) {
                        dispatch(
                            saveStatement({
                                LrsVerb: LrsVerbs.Progressed,
                                response: { zoneId, itemId },
                                zoneId,
                                itemId,
                            })
                        );
                        gotoZoneAndItem(zoneId, itemId);
                    } else {
                        dispatch(
                            saveStatement({
                                LrsVerb: LrsVerbs.Progressed,
                                response: { zoneId },
                                zoneId,
                            })
                        );
                        gotoZone(zoneId);
                    }
                } else {
                    dispatch(
                        saveStatement({
                            LrsVerb: LrsVerbs.Progressed,
                            response: {
                                linkTypeId,
                                linkContent: el.href,
                                autoPlay,
                            },
                        })
                    );

                    // For URL-based learning items with display_mode=external, open in a new tab.
                    // target="_blank" is set by getLearningLinkHtml for external display mode.
                    const elTarget = el.getAttribute('target');
                    if (elTarget === '_blank') {
                        window.open(el.href, '_blank', 'noopener,noreferrer');
                        return;
                    }

                    if (config.modeType === 'author') {
                        dispatch(
                            setLinkDisplayVis({
                                open: true,
                                chapter_id: +el.getAttribute('data-chapter-id') || null,
                                linkContent: el.href,
                                linkTypeId,
                                linkData: { display_name: el.textContent },
                                autoPlay: autoPlay,
                                overlayText: null,
                            })
                        );
                    } else {
                        const chapter_data = findVideoChapter(
                            {
                                content_id: +el.getAttribute('data-target-contentid'),
                                content_version_id: +el.getAttribute('data-target-selectedversion'),
                                chapter_id: +el.getAttribute('data-chapter-id') || null,
                            },
                            dependencyContents || []
                        );
                        dispatch(
                            setLinkDisplayVis({
                                open: true,
                                chapter_data: chapter_data,
                                linkContent: el.href,
                                linkTypeId: linkTypeId,
                                linkData: { display_name: el.textContent },
                                autoPlay: autoPlay,
                                overlayText: null,
                            })
                        );
                    }
                }
            }
        };

        let removeInterceptors = [];
        const attachInterceptors = () => {
            removeInterceptors.forEach((remove) => remove?.());

            const popupDocument = window.geminiProcedurePopup?.document;
            const contentRoots = [document.querySelector(selector), popupDocument?.querySelector(selector)].filter(
                (root, index, roots) => root && roots.indexOf(root) === index
            );

            removeInterceptors = contentRoots.map((root) =>
                interceptClicks(
                    root,
            {
                //
                // Leave all these as defauts:
                //
                // modifierKeys: true
                // download: true
                // target: true
                // hash: true
                // mailTo: true

                // Intercept all clicks, even ones that are not same origin
                sameOrigin: false,
            },
                    handleContentLinkClick
                )
            );
        };

        attachInterceptors();
        window.addEventListener('geminiProcedurePopupReady', attachInterceptors);

        return () => {
            window.removeEventListener('geminiProcedurePopupReady', attachInterceptors);
            removeInterceptors.forEach((remove) => remove?.());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentService, gotoZone, gotoZoneAndItem, dispatch]);
}
